import { BehaviorSubject, firstValueFrom, forkJoin, Subscription } from 'rxjs';
import moment from 'moment';
import { Injector } from '@angular/core';
import { GridApi, IServerSideGetRowsParams } from '@ag-grid-community/core';

import { ScheduleBusinessOrderService } from '../../../../order/services/schedule-business-order.service';
import { DatacenterSelectionService } from '../../../services/datacenter-selection.service';
import { ScheduleBusinessOrder } from '../../../../order/models/schedule-business-order';
import { PaginatedResult } from '../../../models/paginated-result';
import { ColDefWithFields, ColumnType } from '../../column-type';
import { OrderBy, OrderByDirection } from '../../../utils/constant';
import { formatTextDimension } from '../../../utils/number.format';
import { FilterDateFormatType, DATE_FORMAT_PATTERN_DD_MM_YYYY,
	WEEKYEAR_FORMAT_PATTERN } from '../../../models/filter-formats';
import { CommonGridOptions } from '../../grid-options';
import { FavoriteOrders } from '../../../../order/models/favorite-orders';
import { Strategy } from '../strategy';
import { FilterModel } from '../../../models/ag-grid';
import { UserDatacenterView } from '../../../models/datacenter-aggrid-view';

export class StrategyScheduleBusinessOrder implements Strategy<ScheduleBusinessOrder> {
	scheduleBusinessOrderService: ScheduleBusinessOrderService;
	selectionService: DatacenterSelectionService;
	defaultOrderBy = new OrderBy<keyof ScheduleBusinessOrder>('soldTo', OrderByDirection.ASC);
	hasTotal = false;
	aggregateFields = [];

	constructor(injector: Injector) {
		this.scheduleBusinessOrderService = injector.get<ScheduleBusinessOrderService>(ScheduleBusinessOrderService);
		this.selectionService = injector.get<DatacenterSelectionService>(DatacenterSelectionService);
	}

	setTotal(_api?: GridApi, _filter?: string): Subscription {
		throw new Error('Method not implemented.');
	}

	async getAllDataSelected(
		currentColumns: string[],
		filter: string | undefined,
		orderBy: OrderBy<keyof ScheduleBusinessOrder>[],
	) {
		return this.getAllData(orderBy, currentColumns, filter)
			.then((business: PaginatedResult<ScheduleBusinessOrder>) => this.removeDeselectedRows(business));
	}

	async getAllData(
		orderBy: OrderBy<keyof ScheduleBusinessOrder>[],
		currentColumns: string[],
		filter: string | undefined
	) {
		return firstValueFrom(this.scheduleBusinessOrderService.getScheduleBusinessOrders(
			<(keyof ScheduleBusinessOrder)[]> currentColumns,
			filter,
			0,
			60_000,
			orderBy,
			false
		));
	}

	formatCellByType(columnDef: ColDefWithFields<ScheduleBusinessOrder>, data: ScheduleBusinessOrder) {
		const field = columnDef.field || '';
		const fields = columnDef.fields || [];
		const { columnType } = columnDef;
		switch (columnType) {
		case ColumnType.DIMENSION_COLUMN:
			return formatTextDimension(
				<string> data[<keyof ScheduleBusinessOrder> fields[0]],
				<string> data[<keyof ScheduleBusinessOrder> fields[1]],
				<string> data[<keyof ScheduleBusinessOrder> fields[2]]
			);
		case ColumnType.ORDER_NUMBER_COLUMN:
			return <string> data[<keyof ScheduleBusinessOrder> field];
		case ColumnType.NUMBER_COLUMN:
		{
			const value = <number | undefined >data[<keyof ScheduleBusinessOrder> field];
			return value === undefined ? '' : String(value);
		}
		case ColumnType.TEXT_COLUMN:
			return <string> data[<keyof ScheduleBusinessOrder> field];
		case ColumnType.TRANSLATED_COLUMN:
			// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
			return <string> data[<keyof ScheduleBusinessOrder> columnDef.filterParams.saOrderFields[1]];
		case ColumnType.DATE_COLUMN:
			if (data[<keyof ScheduleBusinessOrder> field]) {
				const dateformat = (<FilterDateFormatType> columnDef.cellRendererParams).outputFormat === 'week'
					? WEEKYEAR_FORMAT_PATTERN : DATE_FORMAT_PATTERN_DD_MM_YYYY;
				return moment.utc(<string> data[<keyof ScheduleBusinessOrder> field]).format(dateformat);
			}
			return '';
		default:
			throw new Error('unknown filter type');
		}
	}

	getCustomGridOptions(defaultGridOptions: CommonGridOptions) {
		return new CommonGridOptions<ScheduleBusinessOrder>({
			...defaultGridOptions,
			getRowId: ({ data }: { data: ScheduleBusinessOrder }) => data.supplierOrderNumber,
		});
	}

	getDataFromGrid(
		orderBy: OrderBy<keyof ScheduleBusinessOrder>[],
		currentColumns: (keyof ScheduleBusinessOrder)[],
		filter: string | undefined,
		params: IServerSideGetRowsParams<ScheduleBusinessOrder>,
		rowsCountSubject: BehaviorSubject<number | undefined>,
	) {
		const currentColumnsWithId = this.addIdColumn(currentColumns);
		const getScheduleBusinessOrders = this.scheduleBusinessOrderService.getScheduleBusinessOrders(
			currentColumnsWithId,
			filter,
			params.request.startRow ?? 0,
			250,
			orderBy,
			false
		);
		const getFavoriteScheduleBusinessOrders = this.scheduleBusinessOrderService.getFavoriteScheduleBusinessOrders();
		return forkJoin([getScheduleBusinessOrders, getFavoriteScheduleBusinessOrders]).subscribe({
			next: ([business, favoriteOrders]) => {
				business.results = this.setFavoriteOrders(business.results, favoriteOrders);

				const lastRow = params.request.endRow === undefined || business.count <= params.request.endRow
					? business.count
					: -1;
				params.success({ rowData: business.results, rowCount: lastRow });
				rowsCountSubject.next(business.count);
			},
			error: (err: unknown) => {
				console.error('error requesting schedule business order datacenter', err);
				rowsCountSubject.next(undefined);
				params.fail();
			}
		});
	}

	setFavoriteOrders(scheduleOrder: ScheduleBusinessOrder[], favoriteOrders: FavoriteOrders[]) {
		return scheduleOrder.map(results => {
			const comparedFavoriteNumber = favoriteOrders.find(
				favoriteOrder => favoriteOrder.supplierOrderReference
					=== results.supplierOrderNumber
			);
			results.favorite = comparedFavoriteNumber;
			results.isFavorite = Boolean(comparedFavoriteNumber);

			return results;
		});
	}

	addIdColumn(currentColumns: (keyof ScheduleBusinessOrder)[]) {
		if (!currentColumns.includes('supplierOrderNumber')) {
			currentColumns.push('supplierOrderNumber');
		}
		return currentColumns;
	}

	createAggregateCustomViewFilter(_view: UserDatacenterView): string {
		throw new Error('Method not implemented.');
	}

	createAggregateQuickSearchFilter(
		_currentSelectedView: UserDatacenterView,
		_api?: GridApi,
		_filterModel?: FilterModel
	): string {
		throw new Error('Method not implemented.');
	}

	private removeDeselectedRows(business: PaginatedResult<ScheduleBusinessOrder>) {
		return business.results.filter(businessData => {
			const rowDeselected = this.selectionService.findDeselectedNode(
				businessData.supplierOrderNumber
			);
			return !rowDeselected;
		});
	}
}
